"use client";

import React from "react";
import { cva } from "class-variance-authority";
import { useTheme } from "next-themes";
import { Toaster as Sonner, toast as sonnerToast } from "sonner";
import { X, Check, BadgeInfo, LoaderCircle, TriangleAlert } from "lucide-react";

const Toaster = ({ ...props }) => {
  const { theme = "system" } = useTheme();

  return (
    <Sonner
      theme={theme}
      className="toaster group flex justify-center"
      style={{
        "--width": "30rem",
      }}
      {...props}
    />
  );
};

const toastVariants = cva(
  "flex items-center rounded-lg shadow-lg ring-1 w-full md:max-w-120 py-2 px-3",
  {
    variants: {
      variant: {
        default: "bg-white ring-black/5",
        loading: "bg-white ring-black/5",
        success: "bg-white ring-success/5",
        error: "bg-white ring-danger/5",
        info: "bg-white ring-info/5",
        warning: "bg-white ring-warning/5",
        grayscale: "bg-grayscale-placeholder ring-grayscale/5",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const toastIconVariants = cva("mr-2", {
  variants: {
    variant: {
      default: "text-grayscale-weak",
      loading: "text-primary",
      success: "text-success",
      error: "text-danger",
      info: "text-info",
      warning: "text-warning",
      grayscale: "text-grayscale",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const toastTitleVariants = cva("text-sm font-medium", {
  variants: {
    variant: {
      default: "text-grayscale-label",
      loading: "text-grayscale-label",
      success: "text-success",
      error: "text-danger",
      info: "text-info",
      warning: "text-warning",
      grayscale: "text-grayscale",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const toastSubtitleVariants = cva("mt-1 text-sm", {
  variants: {
    variant: {
      default: "text-grayscale-label",
      loading: "text-grayscale-label",
      success: "text-success",
      error: "text-danger",
      info: "text-info",
      warning: "text-warning",
      grayscale: "text-grayscale",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

function Toast({ id, icon, title, description, variant }) {
  const toastIcon = React.useMemo(() => {
    // if no icon is provided and the variant is not default, then we don't need to render anything
    if (!icon && variant === "default") return null;

    // if there's an icon prop, it will be rendered instead of the variant icon
    if (icon) return icon;

    switch (variant.toUpperCase()) {
      case "LOADING":
        return <LoaderCircle className="animate-spin" />;
      case "SUCCESS":
        return <Check />;
      case "ERROR":
        return <X />;
      case "INFO":
        return <BadgeInfo />;
      case "WARNING":
        return <TriangleAlert />;
    }

    return null;
  }, [icon, variant]);

  return (
    <div id={`sonner-toast-${id}`} className={toastVariants({ variant })}>
      {React.isValidElement(toastIcon) && (
        <div className={toastIconVariants({ variant })}>{toastIcon}</div>
      )}

      <div className="flex flex-1 items-center">
        <div className="w-full">
          {title && <p className={toastTitleVariants({ variant })}>{title}</p>}
          {description && (
            <p className={toastSubtitleVariants({ variant })}>{description}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function toast({ icon, title, description, variant, ...options }) {
  return sonnerToast.custom(
    (id) => (
      <Toast
        id={id}
        icon={icon}
        title={title}
        description={description}
        variant={variant}
      />
    ),
    options
  );
}

export { Toaster, toast };
